import { OnChanges, SimpleChange, Component, Input, EventEmitter, Output } from "@angular/core";

import { DateTime } from "luxon";
import { first, last } from "lodash";
import { MasterDataService } from "../../Services/MasterDataService";
import { PropertyAppFeatures } from "../../Models/GlobalEntities";

enum ResStatusTabs {
    PreArrival = '1',
    Inhouse = '2',
    DueOut = '8',
    CheckedOut = '3'
}

@Component({
    selector: 'SelectPMSDates',
    templateUrl: './SelectPMSDates.html'
})
export class SelectPMSDates implements OnChanges {
    @Input() PropertyId: number;
    @Input() SelectedStatusTab?: ResStatusTabs;
    @Input() Model: any = {};
    @Output() ModelChange = new EventEmitter();

    @Input() BindStartTo = 'StartDate';
    @Input() BindEndTo = 'EndDate';
    @Input() Placeholder = 'PH.PH_SELECT_DATES'

    DatesList: DateTime[] = [];
    SelectedDate?: DateTime;
    TitleLabel: string;
    CurrentPropertyTime: DateTime;

    constructor(private master: MasterDataService) {
        this.CurrentPropertyTime = this.master.GetPropertyTime(this.PropertyId);
    }

    /** resets selection when propertyId changes; */
    ngOnChanges(changes: { [PropertyName: string]: SimpleChange }) {
        const propertyChanges = changes.PropertyId;
        const statusTabChanges = changes.SelectedStatusTab;

        if (propertyChanges || statusTabChanges) {
            const property = (propertyChanges && propertyChanges.currentValue) || this.PropertyId;
            const statusTab = (statusTabChanges && statusTabChanges.currentValue) || this.SelectedStatusTab;

            this.didChangeProperty(property, statusTab);
        }
    }

    /** gets the app features and updates model start, end dates; */
    private didChangeProperty(newPropertyId: number, statusTab: ResStatusTabs) {
        if (!newPropertyId) {
            return;
        }
        this.DatesList = [];
        this.SelectedDate = null;
        console.log('didChangeProperty:', newPropertyId);
        const propertyFeatures = this.master.GetPropertyAppFeatures(newPropertyId);

        if (!propertyFeatures) {
            return;
        }

        this.DatesList = this.getPMSDateRange(newPropertyId, propertyFeatures, statusTab);
        this.updateModelDateRange();
    }

    /** generates master list of dates from app features; */
    private getPMSDateRange(propertyId: number, appFeatures: PropertyAppFeatures, statusTab: ResStatusTabs): DateTime[] {
        const currentPropertyTime = this.master.GetPropertyTime(propertyId);
        const inhouse = [currentPropertyTime];
        const preArrivals = [];
        const checkedOut = [];

        const { PreArrivalDaysLimit, CheckOutDaysLimit } = appFeatures;

        if (CheckOutDaysLimit) {
            for (let i = 1; i < CheckOutDaysLimit; i++) {
                checkedOut.unshift(currentPropertyTime.minus({ days: i }));
            }
        }

        if (PreArrivalDaysLimit) {
            for (let i = 1; i < PreArrivalDaysLimit; i++) {
                preArrivals.push(currentPropertyTime.plus({ days: i }));
            }
        }

        if (!statusTab) {
            return [
                ...checkedOut,
                ...inhouse,
                ...preArrivals
            ]
        }

        switch (statusTab) {
            case ResStatusTabs.PreArrival: return [...inhouse, ...preArrivals];
            case ResStatusTabs.Inhouse: return inhouse;
            case ResStatusTabs.DueOut: return inhouse;
            case ResStatusTabs.CheckedOut: return [...checkedOut, ...inhouse];
        }
    }

    /** updates title string of dropdown trigger button; */
    private updateTitleLabel() {
        if (this.SelectedDate) {
            this.TitleLabel = this.SelectedDate.toFormat('dd MMM');
            return;
        }

        const firstDate = first(this.DatesList);
        const lastDate = last(this.DatesList);

        if (firstDate.hasSame(lastDate, 'day')) {
            this.TitleLabel = firstDate.toFormat('dd MMM');
            return;
        }

        this.TitleLabel = `${firstDate.toFormat("dd MMM")} - ${lastDate.toFormat("dd MMM")} `
    }

    /** updates model's start & end dates based on selectedDate value; */
    private updateModelDateRange() {
        if (!this.Model) {
            return
        }

        this.Model[this.BindStartTo] = null;
        this.Model[this.BindEndTo] = null;

        if (this.SelectedDate) {
            this.Model[this.BindStartTo] =
                this.SelectedDate
                    .startOf('day').toUTC();

            this.Model[this.BindEndTo] =
                this.SelectedDate
                    .endOf('day').toUTC();
        }

        this.ModelChange.emit(this.Model);
        this.updateTitleLabel();
    }

    /** sets selection when date is clicked in dropdown */
    public didTapDate(newDate: DateTime) {
        this.CurrentPropertyTime = this.master.GetPropertyTime(this.PropertyId);

        if (!this.SelectedDate) {
            this.SelectedDate = newDate;
        } else if (this.SelectedDate.toMillis() == newDate.toMillis()) {
            this.SelectedDate = null;
        } else {
            this.SelectedDate = newDate;
        }

        this.updateModelDateRange();
    }

    /** checks if date is today*/
    public isTodayDate(date: DateTime): boolean {
        return this.CurrentPropertyTime.hasSame(date, 'day');
    }

    /** clears selected dated*/
    public resetSelection() {
        this.SelectedDate = null;
        this.updateModelDateRange();
    }

    /** close handler*/
    public didClearSelection() {
        this.resetSelection();
    }

}
